/**
 * strokeToSvgPath: converts raw pointer samples to a smoothed SVG path
 * string using perfect-freehand.
 *
 * M2.T4 per IMPLEMENTATION_PLAN §7 M2.T4 + PRD §6.2.
 * M6 fix: moved from client/src/canvas/strokeToSvgPath.ts to shared so the
 * server can use it in disconnect synthesis (synthesizeStrokeEndForDisconnect)
 * instead of the simpler polyline fallback.
 *
 * This utility is the SINGLE place where `getStroke` is called with the
 * pinned options. It is used identically by:
 *   - The drawer's local canvas renderer (immediate feedback).
 *   - The audience's canvas renderer (replaying received samples).
 *   - The server's disconnect-synthesis path (M2.T20).
 *
 * Using the same function + same options on both sides eliminates the
 * "stroke-end snap": the jarring jump when the audience's live preview
 * is replaced by the final smoothed path, because both are now produced
 * by the exact same algorithm.
 *
 * Pinned options per §7 M2.T4, never change without bumping PROTOCOL_VERSION:
 *   thinning: 0.5
 *   smoothing: 0.5
 *   streamline: 0.5
 *   simulatePressure: false   ← we pass real pressure values when available
 */

import { getStroke } from "perfect-freehand";

/**
 * Base stroke options per §7 M2.T4, never change these without bumping
 * PROTOCOL_VERSION. `size` is NOT included here; pass it as the second
 * argument to strokeToSvgPath() so callers can vary brush width without
 * ctx.scale() misplacement (M5 fix).
 */
export const STROKE_OPTIONS = {
  thinning: 0.5,
  smoothing: 0.5,
  streamline: 0.5,
  simulatePressure: false,
} as const;

/**
 * A point as expected by perfect-freehand:
 *   [x, y, pressure?]: pressure in [0,1], undefined or 0 = no pressure.
 */
export type StrokePoint = [x: number, y: number, pressure?: number];

/**
 * Convert an array of stroke points to a smoothed SVG path string.
 *
 * @param points  Raw pointer samples.
 * @param size    Brush diameter in logical pixels. Passed directly to
 *                perfect-freehand's `size` option so the outline geometry
 *                already encodes the correct width. Do NOT use ctx.scale()
 *                to vary brush width, that scales around the canvas origin
 *                and displaces the stroke geometry (M5 fix).
 *
 * Returns an empty string if `points` is empty (callers should skip
 * rendering empty paths).
 *
 * The output path is an SVG `d` attribute string suitable for use in
 * `<path d="…">` or `ctx.fill(new Path2D("…"))`.
 */
export function strokeToSvgPath(points: StrokePoint[], size = 8): string {
  if (points.length === 0) return "";

  // Cast to the format perfect-freehand accepts; the library accepts
  // [x, y, pressure?] tuples but its type signature requires number[].
  const outlinePoints = getStroke(points as number[][], { ...STROKE_OPTIONS, size });
  return svgPathFromOutline(outlinePoints);
}

/**
 * Converts the outline points array from perfect-freehand into an SVG path
 * string. Uses quadratic bezier curves for smooth rendering.
 *
 * Adapted from the perfect-freehand documentation examples.
 */
function svgPathFromOutline(points: number[][]): string {
  if (points.length === 0) return "";
  if (points.length === 1) {
    const [x, y] = points[0]!;
    return `M ${x} ${y} Z`;
  }

  const d: string[] = [];
  const p0 = points[points.length - 1]!;
  const p1 = points[0]!;

  d.push(`M ${avg(p0[0]!, p1[0]!)} ${avg(p0[1]!, p1[1]!)}`);

  for (let i = 0; i < points.length; i++) {
    const mp = points[i]!;
    const np = points[(i + 1) % points.length]!;
    d.push(`Q ${mp[0]} ${mp[1]} ${avg(mp[0]!, np[0]!)} ${avg(mp[1]!, np[1]!)}`);
  }

  d.push("Z");
  return d.join(" ");
}

function avg(a: number, b: number): number {
  return (a + b) / 2;
}
